import React, { useState, useEffect } from 'react';

const PointsRulesTab = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filters, setFilters] = useState({
    partnerId: '',
    actionType: '',
    isActive: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    partnerId: '',
    name: '',
    description: '',
    actionType: '',
    points: '',
    maxTriggers: '',
    cooldownHours: '',
    isActive: true,
    conditions: {}
  });
  
  // Fetch rules when page, filters or sort changes
  useEffect(() => {
    fetchRules();
  }, [currentPage, itemsPerPage, sortField, sortOrder]);

  const fetchRules = async () => {
    setLoading(true);
    setError(null);

    try {
      // Prepare query parameters
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        sort: sortField,
        order: sortOrder
      });

      // Add filters if they exist
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '') {
          queryParams.append(key, value);
        }
      });

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/points/rules?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch points rules');
      }

      const data = await response.json();
      setRules(data.data);
      setTotalRecords(data.pagination.total);
    } catch (err) {
      console.error('Error fetching points rules:', err);
      setError('Failed to load rules. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to descending for a new field
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when applying new filters
    fetchRules();
  };

  const handleClearFilters = () => {
    setFilters({
      partnerId: '',
      actionType: '',
      isActive: ''
    });
    setCurrentPage(1);
    setSortField('createdAt');
    setSortOrder('desc');
    fetchRules();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOpenModal = (rule = null) => {
    if (rule) {
      // Editing existing rule
      setEditingRule(rule);
      setFormData({
        partnerId: rule.partnerId?._id || rule.partnerId || '',
        name: rule.name || '',
        description: rule.description || '',
        actionType: rule.actionType || '',
        points: rule.points || '',
        maxTriggers: rule.maxTriggers || '',
        cooldownHours: rule.cooldownHours || '',
        isActive: rule.isActive !== undefined ? rule.isActive : true,
        conditions: rule.conditions || {}
      });
    } else {
      // Creating new rule
      setEditingRule(null);
      setFormData({
        partnerId: '',
        name: '',
        description: '',
        actionType: '',
        points: '',
        maxTriggers: '',
        cooldownHours: '',
        isActive: true,
        conditions: {}
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRule(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      const method = editingRule ? 'PUT' : 'POST';
      const url = editingRule 
        ? `/api/admin/points/rules/${editingRule._id}` 
        : '/api/admin/points/rules';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`Failed to ${editingRule ? 'update' : 'create'} rule`);
      }

      // Refresh rules list
      fetchRules();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving rule:', err);
      setError(`Failed to ${editingRule ? 'update' : 'create'} rule. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/points/rules/${ruleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete rule');
      }

      // Refresh rules list
      fetchRules();
    } catch (err) {
      console.error('Error deleting rule:', err);
      setError('Failed to delete rule. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Generate page numbers
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div>
      <h3>Points Rules</h3>
      
      {/* Add new rule button */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => handleOpenModal()}
          style={{
            backgroundColor: '#4f46e5',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            cursor: 'pointer'
          }}
        >
          Add New Rule
        </button>
      </div>
      
      {/* Filters */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
        <form onSubmit={handleApplyFilters}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Partner:</label>
              <input
                type="text"
                name="partnerId"
                value={filters.partnerId}
                onChange={handleFilterChange}
                placeholder="Partner ID"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Action Type:</label>
              <input
                type="text"
                name="actionType"
                value={filters.actionType}
                onChange={handleFilterChange}
                placeholder="Action Type"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Status:</label>
              <select
                name="isActive"
                value={filters.isActive}
                onChange={handleFilterChange}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="submit"
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}
              >
                Apply Filters
              </button>
              
              <button
                type="button"
                onClick={handleClearFilters}
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Rules Table */}
      {loading && !showModal ? (
        <div>Loading rules...</div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('name')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Name
                      {sortField === 'name' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('actionType')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Action Type
                      {sortField === 'actionType' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('points')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Points
                      {sortField === 'points' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>Partner</th>
                  <th style={tableHeaderStyle}>Limits</th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('isActive')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Status
                      {sortField === 'isActive' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('updatedAt')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Last Updated
                      {sortField === 'updatedAt' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px 0' }}>
                      No rules found
                    </td>
                  </tr>
                ) : (
                  rules.map(rule => (
                    <tr key={rule._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={tableCellStyle}>
                        <div style={{ fontWeight: 'bold' }}>{rule.name}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{rule.description}</div>
                      </td>
                      <td style={tableCellStyle}>{rule.actionType}</td>
                      <td style={tableCellStyle}>{rule.points}</td>
                      <td style={tableCellStyle}>
                        {rule.partnerId?.name || rule.partnerId?.substring(0, 8) + '...' || 'N/A'}
                      </td>
                      <td style={tableCellStyle}>
                        {rule.maxTriggers ? (
                          <div>Max triggers: {rule.maxTriggers}</div>
                        ) : null}
                        {rule.cooldownHours ? (
                          <div>Cooldown: {rule.cooldownHours} hours</div>
                        ) : null}
                        {!rule.maxTriggers && !rule.cooldownHours ? 'No limits' : null}
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{
                          backgroundColor: rule.isActive ? '#4CAF50' : '#9E9E9E',
                          color: 'white',
                          borderRadius: '12px',
                          padding: '3px 8px',
                          fontSize: '12px'
                        }}>
                          {rule.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={tableCellStyle}>{formatDate(rule.updatedAt)}</td>
                      <td style={tableCellStyle}>
                        <button
                          onClick={() => handleOpenModal(rule)}
                          style={{
                            backgroundColor: '#059669',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '5px 10px',
                            marginRight: '5px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteRule(rule._id)}
                          style={{
                            backgroundColor: '#DC2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '5px 10px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', alignItems: 'center' }}>
            <div>
              Showing {rules.length} of {totalRecords} entries
            </div>
            <div>
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '5px 10px',
                  marginRight: '5px',
                  backgroundColor: currentPage === 1 ? '#e5e7eb' : '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              
              {pageNumbers.map(number => (
                <button
                  key={number}
                  onClick={() => handlePageChange(number)}
                  style={{
                    padding: '5px 10px',
                    marginRight: '5px',
                    backgroundColor: currentPage === number ? '#4f46e5' : '#f3f4f6',
                    color: currentPage === number ? 'white' : 'black',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {number}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '5px 10px',
                  backgroundColor: currentPage === totalPages ? '#e5e7eb' : '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Modal for Create/Edit Rule */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            width: '600px',
            maxWidth: '90%',
            maxHeight: '90%',
            overflow: 'auto'
          }}>
            <h3>{editingRule ? 'Edit Rule' : 'Create New Rule'}</h3>
            
            {error && <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}
            
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Partner ID*:</label>
                <input
                  type="text"
                  name="partnerId"
                  value={formData.partnerId}
                  onChange={handleFormChange}
                  required
                  style={inputStyle}
                />
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Rule Name*:</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  style={inputStyle}
                />
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Description:</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  style={{ ...inputStyle, height: '80px' }}
                ></textarea>
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Action Type*:</label>
                <input
                  type="text"
                  name="actionType"
                  value={formData.actionType}
                  onChange={handleFormChange}
                  required
                  placeholder="e.g., signup, purchase, visit"
                  style={inputStyle}
                />
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Points*:</label>
                <input
                  type="number"
                  name="points"
                  value={formData.points}
                  onChange={handleFormChange}
                  required
                  min="0"
                  style={inputStyle}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Max Triggers:</label>
                  <input
                    type="number"
                    name="maxTriggers"
                    value={formData.maxTriggers}
                    onChange={handleFormChange}
                    min="1"
                    placeholder="Unlimited"
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Cooldown (hours):</label>
                  <input
                    type="number"
                    name="cooldownHours"
                    value={formData.cooldownHours}
                    onChange={handleFormChange}
                    min="0"
                    placeholder="No cooldown"
                    style={inputStyle}
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleFormChange}
                    style={{ marginRight: '10px' }}
                  />
                  Active
                </label>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    padding: '10px 15px',
                    backgroundColor: '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '10px 15px',
                    backgroundColor: '#4f46e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create Rule')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const tableHeaderStyle = {
  padding: '12px 15px',
  textAlign: 'left',
  borderBottom: '2px solid #d1d5db',
  fontSize: '14px'
};

const tableCellStyle = {
  padding: '12px 15px',
  fontSize: '14px'
};

const inputStyle = {
  width: '100%',
  padding: '8px',
  borderRadius: '4px',
  border: '1px solid #e5e7eb'
};

export default PointsRulesTab;